export default class ESigningFormFiles {
    /**
     * @description Display selected file names below the file input
     * @param {*} form
     * @param {Event} e - Change event from file input
     * @returns void
     */
    static handleFileChange(form, e) {
        if (!e.target.files) return;
        const ul = document.getElementById('uploadedFiles');
        if (!ul) return;
        ul.innerHTML = '';

        const fileInput = e.target;

        for (let i = 0; i < fileInput.files.length; i++) {
            const li = document.createElement('li');
            const fileSize = ESigningFormFiles.returnFileSize(fileInput.files[i].size);
            li.innerHTML = `
                <span class="uploaded-files-list__icon material-icons" aria-hidden="true">description</span>
                <span class="uploaded-files-list__content">
                    <span class="uploaded-files-list__name">${fileInput.files[i].name}</span>
                    <span class="uploaded-files-list__meta">${fileSize}</span>
                </span>
                <button type="button" class="btn-remove-file" data-file-index="${i}" title="Remove file" aria-label="Remove file">
                    <span class="material-icons" aria-hidden="true">close</span>
                </button>
            `;
            ul.appendChild(li);

            const deleteBtn = li.querySelector('.btn-remove-file');
            if (deleteBtn) {
                deleteBtn.addEventListener('click', (evt) => ESigningFormFiles.handleDeleteFileClick(form, fileInput, evt), true);
            }
        }

        ESigningFormFiles.autoFillAgreementNumberFromFileName(form, fileInput);
        ESigningFormFiles.updateDropZoneState();
    }

    /**
     * @description Auto-fill AgreementNumber from filename if the field is empty
     * @param {*} form
     * @param {HTMLInputElement} fileInput - The file input element
     * @returns void
     */
    static autoFillAgreementNumberFromFileName(form, fileInput) {
        const agreementNumberInput = document.getElementById('AgreementNumber');

        if (!agreementNumberInput || agreementNumberInput.value.trim() !== '') {
            return;
        }

        if (!fileInput.files || fileInput.files.length === 0) {
            return;
        }

        let fileName = fileInput.files[0].name;

        if (fileName.toLowerCase().endsWith('.pdf')) {
            fileName = fileName.slice(0, -4);
        }

        fileName = fileName.substring(0, 36);
        agreementNumberInput.value = fileName;

        form.updateStep1NextButtonState();
    }

    /**
     * @description Handle delete button click
     * @param {*} form
     * @param {HTMLInputElement} fileInput - The file input element
     * @param {Event} event - Click event
     * @returns void
     */
    static handleDeleteFileClick(form, fileInput, event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const indexToRemove = parseInt(event.target.closest('.btn-remove-file')?.getAttribute('data-file-index'), 10);
        if (!isNaN(indexToRemove)) {
            ESigningFormFiles.removeFile(form, fileInput, indexToRemove);
        }

        return false;
    }

    /**
     * @description Remove a file from the file input and update the list
     * @param {*} form
     * @param {HTMLInputElement} fileInput - The file input element
     * @param {number} indexToRemove - Index of file to remove
     * @returns void
     */
    static removeFile(form, fileInput, indexToRemove) {
        try {
            const dataTransfer = new DataTransfer();
            const files = Array.from(fileInput.files);

            files.forEach((file, index) => {
                if (index !== indexToRemove) {
                    dataTransfer.items.add(file);
                }
            });

            fileInput.files = dataTransfer.files;

            ESigningFormFiles.handleFileChange(form, { target: fileInput });

            form.updateStep1NextButtonState();
            ESigningFormFiles.updateDropZoneState();
        } catch (error) {
            console.error('Error removing file:', error);
        }
    }

    /**
     * @description Update drop zone copy depending on state
     * @returns void
     */
    static updateDropZoneState() {
        const documentDropZone = document.getElementById('documentDropZone');
        const dropZonePrompt = document.getElementById('documentDropZonePrompt');
        const dropZoneIcon = document.querySelector('#documentDropZone .document-dropzone__icon');
        const documentFileInput = document.getElementById('documentFile');

        if (!documentDropZone || !dropZonePrompt || !documentFileInput) {
            return;
        }

        const promptEmpty =
            documentDropZone.getAttribute('data-prompt-empty') ||
            'Dra och slapp PDF-fil har eller klicka for att valja fil';
        const promptHasFiles =
            documentDropZone.getAttribute('data-prompt-has-files') ||
            'En PDF-fil ar vald. Valj en ny fil for att ersatta den';

        if (documentFileInput.files && documentFileInput.files.length > 0) {
            documentDropZone.classList.add('has-files');
            dropZonePrompt.textContent = promptHasFiles;
            dropZonePrompt.style.display = 'none';
            if (dropZoneIcon) {
                dropZoneIcon.style.display = 'none';
            }
            return;
        }

        documentDropZone.classList.remove('has-files');
        dropZonePrompt.textContent = promptEmpty;
        dropZonePrompt.style.display = '';
        if (dropZoneIcon) {
            dropZoneIcon.style.display = '';
        }
    }

    /**
     * @description Format file size for display
     * @param {number} number - Size in bytes
     * @returns {string}
     */
    static returnFileSize(number) {
        if (number < 1024) return `${number} bytes`;
        if (number < 1048576) return `${(number / 1024).toFixed(1)} KB`;
        return `${(number / 1048576).toFixed(1)} MB`;
    }

    /**
     * @description Get PDF file as byte array (base64)
     * @returns {Promise<string|null>}
     */
    static async getPdfFileBytes() {
        const fileInput = document.getElementById('documentFile');
        if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
            return null;
        }

        const file = fileInput.files[0];
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });
    }
}